import React from "react";

interface Scl90ScoreDisplayProps {
  scores: Record<string, any>;
  interpretations: Record<string, any>;
}

const Scl90ScoreDisplay: React.FC<Scl90ScoreDisplayProps> = ({ 
  scores, 
  interpretations 
}) => {
  // Calculate overall indices
  const calculateIndices = () => {
    if (!scores) return null;

    // Get all dimensions except 'items_adicionales'
    const dimensions = Object.entries(scores).filter(([key]) => key !== 'items_adicionales');
    
    // Global Severity Index (GSI) - Average of all items
    let totalScore = 0;
    let totalItems = 0;
    
    dimensions.forEach(([_, value]: [string, any]) => {
      totalScore += value.raw_score || 0;
      totalItems += value.item_count || 0;
    });
    
    const gsi = totalItems > 0 ? totalScore / totalItems : 0;
    
    // Positive Symptom Total (PST) - Number of items endorsed with a score > 0
    // Since we don't have access to individual item scores, we'll estimate based on averages
    const pst = dimensions.reduce((sum, [_, value]: [string, any]) => {
      const average = value.average || 0;
      const itemCount = value.item_count || 0;
      // Estimate number of non-zero items
      return sum + (average > 0 ? Math.min(itemCount, Math.ceil(itemCount * average)) : 0);
    }, 0);
    
    // Positive Symptom Distress Index (PSDI) - Average score of items endorsed > 0
    const psdi = pst > 0 ? totalScore / pst : 0;
    
    return {
      gsi: parseFloat(gsi.toFixed(2)),
      pst,
      psdi: parseFloat(psdi.toFixed(2))
    };
  };

  // Helper function to get severity level description
  const getSeverityLevel = (average: number): string => {
    if (average <= 0.5) return "Ausente o mínimo";
    if (average <= 1.0) return "Leve";
    if (average <= 2.0) return "Moderado";
    if (average <= 3.0) return "Alto";
    return "Muy alto";
  };

  // Helper function to get color based on average score
  const getScoreColor = (average: number): string => {
    if (average <= 0.5) return "text-green-500";
    if (average <= 1.0) return "text-blue-500";
    if (average <= 2.0) return "text-yellow-500";
    if (average <= 3.0) return "text-orange-500";
    return "text-red-500";
  };

  // Helper function to get background color based on average score
  const getBarColor = (average: number): string => {
    if (average <= 0.5) return "bg-green-500";
    if (average <= 1.0) return "bg-blue-500";
    if (average <= 2.0) return "bg-yellow-500";
    if (average <= 3.0) return "bg-orange-500";
    return "bg-red-500";
  };

  // Helper function to get interpretation text
  const getInterpretationText = (key: string, average: number): string => {
    // If we have an interpretation from the API, use that
    if (interpretations && interpretations[key]) {
      return interpretations[key];
    }
    
    // Otherwise generate a default interpretation based on average score
    return getSeverityLevel(average);
  };
  
  // Calculate SCL-90 indices
  const indices = calculateIndices();

  // Get dimension names in Spanish
  const getDimensionName = (key: string): string => {
    const dimensionNames: Record<string, string> = {
      somatizacion: "Somatización",
      obsesiones: "Obsesiones y Compulsiones",
      sensibilidad_interpersonal: "Sensibilidad Interpersonal",
      depresion: "Depresión",
      ansiedad: "Ansiedad",
      hostilidad: "Hostilidad",
      ansiedad_fobica: "Ansiedad Fóbica",
      ideacion_paranoide: "Ideación Paranoide",
      psicoticismo: "Psicoticismo",
      items_adicionales: "Ítems Adicionales"
    };
    
    return dimensionNames[key] || key;
  };

  // Get dimension descriptions
  const getDimensionDescription = (key: string): string => {
    const descriptions: Record<string, string> = {
      somatizacion: "Malestar percibido relacionado con disfunciones corporales cardiovasculares, gastrointestinales, respiratorias y otras con fuerte mediación del sistema nervioso autónomo.",
      obsesiones: "Pensamientos, acciones e impulsos que son vivenciados como imposibles de evitar o no deseados.",
      sensibilidad_interpersonal: "Sentimientos de inferioridad e inadecuación personal, auto-depreciación e incomodidad durante las interacciones personales.",
      depresion: "Estado de ánimo disfórico, falta de motivación, poca energía vital, sentimientos de desesperanza e ideaciones suicidas.",
      ansiedad: "Nerviosismo, tensión, ataques de pánico, miedos.",
      hostilidad: "Pensamientos, sentimientos y acciones característicos de la presencia de estados afectivos negativos de ira, irritabilidad, rabia y resentimiento.",
      ansiedad_fobica: "Miedo persistente, irracional y desproporcionado a personas específicas, lugares, objetos o situaciones.",
      ideacion_paranoide: "Pensamiento proyectivo, suspicacia, temor a la pérdida de autonomía.",
      psicoticismo: "Síntomas referidos a estados de soledad, estilo de vida esquizoide, alucinaciones y control del pensamiento.",
      items_adicionales: "Ítems que cargan en varios factores (problemas de sueño, pensamientos sobre la muerte, sentimientos de culpa)."
    };
    
    return descriptions[key] || "";
  };
  
  // Debug log
  console.log("SCL-90 scores:", scores);
  console.log("SCL-90 interpretations:", interpretations);
  console.log("SCL-90 calculated indices:", indices);

  // If scores is empty or not an object, display a message
  if (!scores || typeof scores !== 'object' || Object.keys(scores).length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-gray-600 dark:text-gray-400">
          No hay puntuaciones disponibles para SCL-90
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Datos recibidos: {JSON.stringify(scores)}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Summary section */}
      <div className="bg-white dark:bg-navy-800 rounded-xl p-5 shadow-md mb-6">
        <h5 className="text-lg font-bold text-navy-700 dark:text-white mb-2">
          SCL-90 (Symptom Checklist-90)
        </h5>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Evalúa patrones de síntomas psicológicos en pacientes y población general
        </p>
        
        {indices && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="bg-gray-50 dark:bg-navy-700 p-3 rounded-lg">
              <h6 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Índice Global de Severidad (GSI)
              </h6>
              <p className={`text-xl font-bold ${getScoreColor(indices.gsi)}`}>
                {indices.gsi}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Intensidad del malestar psíquico global
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-navy-700 p-3 rounded-lg">
              <h6 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Total de Síntomas Positivos (PST)
              </h6>
              <p className="text-xl font-bold text-navy-700 dark:text-white">
                {indices.pst}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Número total de síntomas con puntuación positiva
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-navy-700 p-3 rounded-lg">
              <h6 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Índice de Malestar Sintomático (PSDI)
              </h6>
              <p className={`text-xl font-bold ${getScoreColor(indices.psdi)}`}>
                {indices.psdi}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Intensidad sintomática media de los síntomas presentes
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Dimensions */}
      <div className="space-y-4">
        <h5 className="text-lg font-bold text-navy-700 dark:text-white mb-2">
          Dimensiones Sintomáticas
        </h5>
        
        <div className="space-y-4">
          {Object.entries(scores)
            .sort(([, a]: [string, any], [, b]: [string, any]) => (b.average || 0) - (a.average || 0)) // Sort by average score (descending)
            .map(([key, score]: [string, any]) => {
              const average = score.average || 0;
              const rawScore = score.raw_score || 0;
              const itemCount = score.item_count || 0;
              const interpretationText = getInterpretationText(key, average);
              const textColor = getScoreColor(average);
              const barColor = getBarColor(average);
              
              return (
                <div key={key} className="bg-white dark:bg-navy-800 rounded-lg p-4 shadow-sm">
                  <div className="flex flex-wrap justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {getDimensionName(key)}
                    </span>
                    <div className="flex items-center">
                      <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">
                        {average.toFixed(2)}
                      </span>
                      <span className={`text-sm font-medium ${textColor}`}>
                        {interpretationText}
                      </span>
                    </div>
                  </div>
                  
                  <div className="w-full bg-gray-200 dark:bg-navy-700 rounded-full h-2.5 mb-2">
                    <div 
                      className={`h-2.5 rounded-full ${barColor}`} 
                      style={{ width: `${Math.max(5, Math.min(100, (average / 4) * 100))}%` }}
                    ></div>
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-500 px-1 mb-2">
                    <span>0</span>
                    <span>1</span>
                    <span>2</span>
                    <span>3</span>
                    <span>4</span>
                  </div>
                  
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {getDimensionDescription(key)}
                  </p>
                  
                  <div className="flex items-start mt-3">
                    <div className="text-xs bg-gray-100 dark:bg-navy-900 px-2 py-1 rounded mr-2">
                      Puntuación bruta: {rawScore}/{itemCount * 4}
                    </div>
                    <div className="text-xs bg-gray-100 dark:bg-navy-900 px-2 py-1 rounded">
                      Ítems: {itemCount}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white dark:bg-navy-800 rounded-xl p-5 shadow-md">
        <h5 className="text-md font-bold text-navy-700 dark:text-white mb-3">
          Interpretación de Puntuaciones
        </h5>
        <div className="space-y-3">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
            <span className="text-sm">
              <span className="font-medium">0.00 - 0.50:</span> Sintomatología ausente o mínima
            </span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
            <span className="text-sm">
              <span className="font-medium">0.51 - 1.00:</span> Sintomatología leve
            </span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
            <span className="text-sm">
              <span className="font-medium">1.01 - 2.00:</span> Sintomatología moderada
            </span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-orange-500 mr-2"></div>
            <span className="text-sm">
              <span className="font-medium">2.01 - 3.00:</span> Sintomatología alta
            </span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
            <span className="text-sm">
              <span className="font-medium">3.01 - 4.00:</span> Sintomatología muy alta
            </span>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h6 className="text-sm font-semibold mb-2">Acerca de las puntuaciones SCL-90</h6>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Las puntuaciones se basan en una escala de 0-4 donde 0 = "Nada" y 4 = "Mucho". 
            La puntuación media (average) para cada dimensión indica la intensidad de los síntomas 
            en esa área específica. El Índice Global de Severidad (GSI) es una medida general 
            de la intensidad del sufrimiento psíquico.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Scl90ScoreDisplay; 